/**
 * Habilidades das posições (item 4.4). Aqui fica o que acontece no atleta: recarga, duração, o drible da Finta
 * Espiral, o impulso do Mergulho e a queda dele no chão. O efeito nas bolas (imunidade a Petardo, área do
 * goleiro, Rebatida Dupla, mão do caçador) está em `balls.ts`, que consulta `abilityActive`.
 */
import { ABILITIES, ABILITY_OF, MATCH, NET, type AbilityId } from "@besenliga/shared";
import type { PlayEvent } from "./balls.js";
import type { CollisionEvent } from "./collision.js";
import type { AthleteState } from "./flight.js";
import { BUTTON, type InputFrame } from "./input.js";

const ticks = (seconds: number) => Math.round(seconds * NET.simTickHz);

/** Duração do efeito, em ticks (a Rebatida Dupla é instantânea: vale só no tick em que é usada). */
export function abilityDuration(id: AbilityId): number {
  switch (id) {
    case "finta":
      return ticks(ABILITIES.finta.seconds);
    case "muralha":
      return ticks(ABILITIES.muralha.seconds);
    case "mergulho":
      return ticks(ABILITIES.mergulho.seconds);
    case "rebatidaDupla":
      return 1;
  }
}

/** A habilidade da posição do atleta está valendo neste tick. */
export function abilityActive(a: AthleteState, id: AbilityId): boolean {
  return a.abilityTicks > 0 && ABILITY_OF[a.role] === id;
}

/**
 * Antes do voo: conta recarga e duração e dispara a habilidade de quem apertou o botão (borda), não está
 * atordoado e já recarregou. Altera `athletes` e devolve quem usou neste tick.
 */
export function startAbilities(
  athletes: AthleteState[],
  inputs: InputFrame,
): { activated: Set<string>; plays: PlayEvent[] } {
  const activated = new Set<string>();
  const plays: PlayEvent[] = [];
  for (const a of athletes) {
    if (a.abilityCooldown > 0) a.abilityCooldown -= 1;
    if (a.abilityTicks > 0) a.abilityTicks -= 1;
    const cmd = inputs[a.id];
    const now = cmd?.buttons ?? 0;
    const pressed = (now & BUTTON.ability) !== 0 && (a.buttons & BUTTON.ability) === 0;
    if (!pressed || a.stunTicks > 0 || a.abilityCooldown > 0) continue;
    const id = ABILITY_OF[a.role];
    a.abilityCooldown = ticks(MATCH.abilityCooldownSeconds);
    a.abilityTicks = abilityDuration(id);
    activated.add(a.id);
    plays.push({ kind: "habilidade", athlete: a.id });

    if (id === "finta") {
      // Para o lado que o direcional aponta (sem direcional: para a esquerda da vassoura).
      const cross = cmd ? a.heading.x * cmd.z - a.heading.z * cmd.x : 0;
      const side = cross > 0 ? -1 : 1;
      // Esquerda da vassoura (guinada positiva) = (h.z, -h.x).
      a.velocity.x += side * a.heading.z * ABILITIES.finta.sideSpeed;
      a.velocity.z += side * -a.heading.x * ABILITIES.finta.sideSpeed;
    } else if (id === "mergulho") {
      const m = ABILITIES.mergulho;
      const along = a.velocity.x * a.heading.x + a.velocity.z * a.heading.z;
      const boost = Math.max(0, m.forwardSpeed - along);
      a.velocity.x += a.heading.x * boost;
      a.velocity.z += a.heading.z * boost;
      a.velocity.y = Math.min(a.velocity.y, -m.downSpeed);
    }
  }
  return { activated, plays };
}

/** Depois das colisões: o caçador que mergulhou e bateu no chão rápido demais fica atordoado. */
export function afterCollisions(
  athletes: AthleteState[],
  collisions: readonly CollisionEvent[],
): PlayEvent[] {
  const plays: PlayEvent[] = [];
  for (const c of collisions) {
    if (c.kind !== "chao" || c.impactSpeed < ABILITIES.mergulho.crashSpeed) continue;
    const a = athletes.find((x) => x.id === c.athlete);
    if (!a || !abilityActive(a, "mergulho") || a.stunTicks > 0) continue;
    a.stunTicks = ticks(ABILITIES.mergulho.crashStunSeconds);
    a.abilityTicks = 0;
    a.velocity = { x: a.velocity.x * 0.2, y: 0, z: a.velocity.z * 0.2 };
    plays.push({ kind: "queda", athlete: a.id });
  }
  return plays;
}
